import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  getBannerAspectRatio,
  getBannerDisplayPixels,
} from "../../constants/masterBanner";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const BANNER_SLOT_CONFIG = {
  home: {
    bannerType: "HOME",
    className: "",
    hideBlurredBackground: false,
    fixedAspect: false,
  },
  mid: {
    bannerType: "SPONSORED",
    className: "hub-banner-carousel--page-strip hub-banner-carousel--mid",
    hideBlurredBackground: true,
    autoHeight: false,
    hideCaptionOnImage: true,
  },
  promo: {
    bannerType: "PROMO",
    className: "hub-banner-carousel--page-strip hub-banner-carousel--bottom",
    hideBlurredBackground: true,
    autoHeight: false,
    hideCaptionOnImage: true,
  },
};

function BannerSlide({ banner, hideBlurredBackground = false, displayWidth, displayHeight, hideCaption = false }) {
  const title = String(banner.title || "").trim();
  const description = String(banner.description || "").trim();
  const hasCaption = Boolean(title || description) && !hideCaption;
  const desktopUrl = resolvePublicFileUrl(banner.image);
  const mobileUrl = banner.mobile_image ? resolvePublicFileUrl(banner.mobile_image) : "";
  const imageUrl = desktopUrl || mobileUrl;

  const slide = (
    <div className="hub-banner-carousel__slide">
      {!hideBlurredBackground && desktopUrl ? (
        <div
          className="hub-banner-carousel__image-bg"
          style={{ backgroundImage: `url("${desktopUrl}")` }}
          aria-hidden
        />
      ) : null}
      {desktopUrl || mobileUrl ? (
        <picture>
          {mobileUrl ? (
            <source media="(max-width: 768px)" srcSet={mobileUrl} />
          ) : null}
          <img
            className="hub-banner-carousel__image"
            src={imageUrl}
            alt={title || "Banner"}
            loading="lazy"
            decoding="async"
            width={displayWidth || undefined}
            height={displayHeight || undefined}
          />
        </picture>
      ) : null}
      {hasCaption ? (
        <div className="hub-banner-carousel__overlay">
          <div className="hub-banner-carousel__caption">
            {title ? <h2 className="hub-banner-carousel__title">{title}</h2> : null}
            {description ? <p className="hub-banner-carousel__description">{description}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  const url = String(banner.redirect_url || "").trim();
  if (!url) return slide;

  if (url.startsWith("/")) {
    return (
      <Link to={url} className="hub-banner-carousel__link">
        {slide}
      </Link>
    );
  }

  return (
    <a href={url} className="hub-banner-carousel__link" target="_blank" rel="noopener noreferrer">
      {slide}
    </a>
  );
}

export default function HubHomeBannerCarousel({
  banners,
  className = "",
  ariaLabel = "Home banner slider",
  requireImage = true,
  hideBlurredBackground = true,
  autoHeight = false,
  /** CSS aspect-ratio value, e.g. "1920 / 270" — overrides slot default when set */
  aspectRatio = "",
  /** home | mid | promo — applies slot class, aspect ratio, and display dimensions */
  bannerSlot = "",
}) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const slotKey = String(bannerSlot || "").toLowerCase();
  const slot = BANNER_SLOT_CONFIG[slotKey] || null;

  const items = Array.isArray(banners)
    ? banners.filter((b) => (requireImage ? b?.image : b?.image || b?.title))
    : [];
  if (!items.length) return null;

  const loop = items.length > 1;
  const isPageStrip = slotKey === "mid" || slotKey === "promo";
  const slotClassName = slot?.className || "";
  const resolvedHideBlur =
    slot != null ? slot.hideBlurredBackground : hideBlurredBackground;
  const resolvedAutoHeight =
    slot != null && Object.prototype.hasOwnProperty.call(slot, "autoHeight")
      ? slot.autoHeight
      : autoHeight;
  const bannerType = slot?.bannerType || "HOME";
  const resolvedAspect =
    String(aspectRatio || "").trim() ||
    (slot?.fixedAspect ? getBannerAspectRatio(bannerType) : "");
  const displayPixels = isPageStrip
    ? getBannerDisplayPixels(bannerType)
    : slot?.fixedAspect
      ? getBannerDisplayPixels(bannerType)
      : null;

  const isMarketplace = className.includes("hub-banner-carousel--marketplace");
  const sectionClass = [
    "hub-banner-carousel",
    slotClassName,
    className,
    !isPageStrip &&
    resolvedHideBlur &&
    !isMarketplace &&
    !resolvedAutoHeight &&
    !resolvedAspect
      ? "hub-banner-carousel--letterbox"
      : "",
    !isPageStrip && resolvedAspect ? "hub-banner-carousel--fixed-aspect" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sectionStyle = isPageStrip
    ? { "--strip-aspect": getBannerAspectRatio(bannerType) }
    : resolvedAspect && String(resolvedAspect).trim()
      ? { "--banner-aspect": String(resolvedAspect).trim() }
      : undefined;

  const sectionDataAttrs = isPageStrip
    ? {
        "data-banner-slot": slotKey,
        "data-banner-width": String(displayPixels?.width ?? ""),
        "data-banner-height": String(displayPixels?.height ?? ""),
      }
    : undefined;

  return (
    <section
      className={sectionClass}
      style={sectionStyle}
      aria-label={ariaLabel}
      {...sectionDataAttrs}
    >
      {loop ? (
        <>
          <button
            ref={prevRef}
            type="button"
            className="hub-banner-carousel__nav hub-banner-carousel__nav--prev"
            aria-label="Previous slide"
          >
            <FaChevronLeft aria-hidden />
          </button>
          <button
            ref={nextRef}
            type="button"
            className="hub-banner-carousel__nav hub-banner-carousel__nav--next"
            aria-label="Next slide"
          >
            <FaChevronRight aria-hidden />
          </button>
        </>
      ) : null}

      <Swiper
        modules={[Autoplay, Navigation, Pagination]}
        slidesPerView={1}
        spaceBetween={0}
        loop={loop}
        autoHeight={resolvedAutoHeight}
        autoplay={
          loop
            ? {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        pagination={{ clickable: true }}
        navigation={
          loop
            ? {
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }
            : false
        }
        onBeforeInit={(swiper) => {
          if (!loop) return;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
        }}
        onInit={(swiper) => {
          if (!loop || !prevRef.current || !nextRef.current) return;
          swiper.params.navigation.prevEl = prevRef.current;
          swiper.params.navigation.nextEl = nextRef.current;
          swiper.navigation.init();
          swiper.navigation.update();
        }}
        grabCursor
        touchEventsTarget="container"
      >
        {items.map((banner) => (
          <SwiperSlide key={banner.id}>
            <BannerSlide
              banner={banner}
              hideBlurredBackground={resolvedHideBlur}
              displayWidth={isPageStrip ? undefined : displayPixels?.width}
              displayHeight={isPageStrip ? undefined : displayPixels?.height}
              hideCaption={Boolean(slot?.hideCaptionOnImage && banner?.image)}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  );
}
